import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Facebook, Twitter, Instagram, Linkedin, Music2 } from "lucide-react";
import { useBranding } from "@/contexts/BrandingProvider";

const defaultSocialLinks = [
  { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
  { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
  { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
  { icon: Linkedin, href: "https://linkedin.com", label: "LinkedIn" },
];

export const MainFooter: React.FC = () => {
  const { t } = useTranslation();
  const { branding, isBranded } = useBranding();

  const brandedSocials = useMemo(() => {
    if (!isBranded || !branding) return [];
    const candidates: Array<{ icon: typeof Facebook; href: string; label: string }> = [];
    if (branding.socialFacebook) candidates.push({ icon: Facebook, href: branding.socialFacebook, label: "Facebook" });
    if (branding.socialInstagram) candidates.push({ icon: Instagram, href: branding.socialInstagram, label: "Instagram" });
    if (branding.socialTwitter) candidates.push({ icon: Twitter, href: branding.socialTwitter, label: "Twitter" });
    if (branding.socialLinkedin) candidates.push({ icon: Linkedin, href: branding.socialLinkedin, label: "LinkedIn" });
    // WHY Music2: lucide-react ships no TikTok glyph; Music2 reads as "music/video" and
    // is the closest semantic match without pulling a custom SVG into the codebase.
    if (branding.socialTiktok) candidates.push({ icon: Music2, href: branding.socialTiktok, label: "TikTok" });
    return candidates;
  }, [branding, isBranded]);

  return (
    <footer className="relative border-t backdrop-blur-md bg-background/80">
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            {isBranded && branding ? (
              <img
                src={branding.logoUrl}
                alt={branding.displayName}
                className="h-8 w-auto"
                data-testid="branded-footer-logo"
              />
            ) : (
              <>
                <img src="/Logo.svg" alt="Guardian Logo" className="h-8 w-auto dark:hidden" />
                <img src="/Logo2.svg" alt="Guardian Logo" className="h-8 w-auto hidden dark:block" />
              </>
            )}
            <div className="h-4 w-px bg-border hidden md:block" />
            <div className="flex flex-col">
              <p className="text-sm text-muted-foreground text-center md:text-left">
                © {new Date().getFullYear()} {isBranded && branding ? branding.displayName : "GuardianCheckin"}.{" "}
                {t("footer.allRightsReserved")}
              </p>
              {isBranded ? (
                <a
                  href="https://www.guardiancheckin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground/60 hover:text-muted-foreground text-center md:text-left"
                  data-testid="powered-by-link"
                >
                  {t("footer.poweredBy")}
                </a>
              ) : (
                <p className="text-xs text-muted-foreground/70 text-center md:text-left">
                  {t("footer.verifiedBy")}
                </p>
              )}
            </div>
          </div>

          {isBranded ? (
            brandedSocials.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-6"
                data-testid="branded-socials"
              >
                {brandedSocials.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-4 h-4" />
                    <span className="sr-only">{social.label}</span>
                  </motion.a>
                ))}
              </motion.div>
            )
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-6"
            >
              {defaultSocialLinks.map((social) => (
                <motion.a
                  key={social.href}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon className="w-4 h-4" />
                  <span className="sr-only">{social.label}</span>
                </motion.a>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </footer>
  );
};
